/**
 * PHARMAKON — Forgot Password (Phase 1)
 *
 * Public page. Submits the email to POST /auth/forgot-password, then ALWAYS
 * shows the same neutral confirmation (no account enumeration). Visual style
 * matches the login page: brand canvas, Wordmark, the v3 Field/Input/Button
 * primitives.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Wordmark,
  Field,
  Input,
  Button,
  Icon,
} from "@/components/ui/primitives";
import { authApi } from "@/services/index.js";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
    } catch {
      // The endpoint is intentionally non-revealing; even on error we show the
      // same neutral confirmation so the email's existence is never leaked.
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <main className="min-h-screen brand-canvas-bg flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <Wordmark size="lg" />
        </div>

        <div className="bg-surface border border-border-subtle rounded-xl shadow-pop p-8">
          {!sent ? (
            <>
              <h1 className="text-2xl font-semibold tracking-tight text-text-strong mb-1.5">
                Mot de passe oublié&nbsp;?
              </h1>
              <p className="text-sm text-text-muted mb-8">
                Saisissez votre adresse e-mail. Si un compte y est associé, vous
                recevrez un lien de réinitialisation.
              </p>

              <form onSubmit={submit} className="space-y-5">
                <Field label="Adresse e-mail" required>
                  <Input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    icon="mail"
                    placeholder="vous@votre-pharmacie.dz"
                    size="lg"
                    required
                    autoFocus
                  />
                </Field>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  loading={loading}
                  disabled={loading || !email.trim()}
                >
                  Envoyer le lien de réinitialisation
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-success-soft text-success flex items-center justify-center">
                <Icon name="mail-check" size={26} />
              </div>
              <h1 className="text-xl font-semibold text-text-strong mb-2">
                Vérifiez votre boîte de réception
              </h1>
              <p className="text-sm text-text-muted mb-1">
                Si un compte existe pour{" "}
                <span className="font-medium text-text-default break-all">
                  {email.trim().toLowerCase()}
                </span>
                , un e-mail contenant un lien sécurisé vient d'être envoyé.
              </p>
              <p className="text-xs text-text-muted mb-6">
                Le lien expire dans 60&nbsp;minutes. Pensez à vérifier vos
                courriers indésirables.
              </p>
              <Button
                variant="secondary"
                size="md"
                className="w-full"
                onClick={() => navigate("/login")}
              >
                Retour à la connexion
              </Button>
            </div>
          )}
        </div>

        {!sent && (
          <p className="text-center text-sm text-text-muted mt-6">
            <Link
              to="/login"
              className="text-accent hover:underline inline-flex items-center gap-1.5"
            >
              <Icon name="arrow-left" size={14} />
              Retour à la connexion
            </Link>
          </p>
        )}
      </div>
    </main>
  );
}
